/*
  h26-events.js: Harvest 2026 event helper.
  Loaded only through snippets/h26-head.liquid (opt-in pages).

  Publishes Shopify custom events: Shopify.analytics.publish('oo_<event>', payload).
  The OO app pixel receives them (with consent) and lands them raw in BigQuery.
  If nothing listens, or Shopify.analytics is missing, events are dropped. Never throws.

  Every payload carries the common fields:
    page, asset_id, placement, product_id, market, device, ig_id, arm, change_id, client_ts
  Extra fields come only from a fixed list (EXTRA below) and only as short token values
  (letters, digits and _ . : / - , at most 64 chars). No free text, no personal data.

  Seen: an element with [data-asset-id] counts as seen when 50% of it is visible for 1 s,
  once per asset per page view. Event name from data-h26-seen (default section_view;
  'none' disables). Assets added later are picked up automatically.

  Clicks: any element with data-h26-event="<name>" publishes oo_<name> on click.
  Extra fields from data-h26-<field> attributes on that element.

  API for page scripts:
    OOH26.track(name, fields, el)   publishes oo_<name>; el gives asset/placement context
    OOH26.context(el)               the common fields for an element
    OOH26.observe(root)             scan a subtree for new [data-asset-id] elements
  Debug: localStorage oo_h26_debug = 1 logs every event to the console.
*/
(function () {
  'use strict';
  if (window.OOH26 && window.OOH26.__events) return;

  var CFG = window.OO_H26 || {};
  var EXTRA = ['from', 'to', 'method', 'carousel', 'step', 'topic', 'tab', 'sort', 'zone',
    'farmer_id', 'variant_id', 'quantity', 'outcome', 'status', 'error_code', 'source',
    'added_qty', 'index', 'count', 'value', 'state', 'target', 'utm_source', 'utm_medium',
    'utm_campaign', 'utm_content', 'utm_term', 'ad_id', 'adset_id'];
  var TOKEN = /^[A-Za-z0-9_.:\/\-]{0,64}$/;
  var NAME = /^[a-z][a-z0-9_]{1,40}$/;
  var SEEN_MS = 1000;
  var SEEN_RATIO = 0.5;
  var queue = [];
  var debug = false;
  try { debug = window.localStorage.getItem('oo_h26_debug') === '1'; } catch (e) {}

  function clean(v) {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number') return isFinite(v) ? v : null;
    if (typeof v === 'boolean') return v;
    v = String(v);
    return TOKEN.test(v) ? v : null;
  }

  function attr(el, name) {
    var n = el && el.closest ? el.closest('[' + name + ']') : null;
    return n ? n.getAttribute(name) : null;
  }

  function device() {
    try {
      var w = window.innerWidth || document.documentElement.clientWidth || 0;
      var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      if (w < 768) return 'mobile';
      if (w < 1100 && coarse) return 'tablet';
      return 'desktop';
    } catch (e) { return null; }
  }

  function cookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[-.]/g, '\\$&') + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }

  function igId() {
    try {
      var d = window.igData;
      if (d && d.user && d.user.igId) return String(d.user.igId);
    } catch (e) {}
    return cookie('ig-id');
  }

  // Intelligems assignments as "<test id>:<group id>" joined by "|" (ids only, no names).
  function igArm() {
    try {
      var u = window.igData && window.igData.user;
      if (!u || typeof u.getExperiments !== 'function') return null;
      var out = [];
      (u.getExperiments() || []).forEach(function (x) {
        if (!x || !x.id || x.isPreview) return;
        var g = typeof u.getTestGroup === 'function' ? u.getTestGroup(x.id) : null;
        if (g && g.id) out.push(x.id + ':' + g.id);
      });
      return out.length ? out.join('|') : null;
    } catch (e) { return null; }
  }

  function context(el) {
    var arm = attr(el, 'data-h26-arm') || CFG.arm || null;
    var ig = igArm();
    var armVal = arm && ig ? arm + '|' + ig : (arm || ig);
    return {
      page: clean(attr(el, 'data-h26-page') || CFG.page),
      asset_id: clean(attr(el, 'data-asset-id')),
      placement: clean(attr(el, 'data-placement')),
      product_id: clean(attr(el, 'data-h26-product-id') || CFG.productId),
      market: clean(CFG.market),
      device: device(),
      ig_id: clean(igId()),
      arm: armVal ? String(armVal).slice(0, 512) : null,
      change_id: clean(attr(el, 'data-change-id') || CFG.changeId),
      client_ts: new Date().toISOString()
    };
  }

  function publishNow(name, payload) {
    var a = window.Shopify && window.Shopify.analytics;
    if (!a || typeof a.publish !== 'function') return false;
    try { a.publish(name, payload); } catch (e) {}
    return true;
  }

  function flush() {
    while (queue.length) {
      if (!publishNow(queue[0][0], queue[0][1])) return;
      queue.shift();
    }
  }

  function track(name, fields, el) {
    try {
      name = String(name || '').replace(/^oo_/, '');
      if (!NAME.test(name)) return;
      var payload = context(el || null);
      if (fields && typeof fields === 'object') {
        for (var i = 0; i < EXTRA.length; i++) {
          var k = EXTRA[i];
          if (Object.prototype.hasOwnProperty.call(fields, k)) {
            var v = clean(fields[k]);
            if (v !== null) payload[k] = v;
          }
        }
        // explicit overrides of common context fields, still token-checked
        ['asset_id', 'placement', 'product_id'].forEach(function (k) {
          if (fields[k] !== undefined) payload[k] = clean(fields[k]);
        });
      }
      var full = 'oo_' + name;
      if (debug) { try { console.debug('[h26]', full, payload); } catch (e) {} }
      if (!publishNow(full, payload)) {
        if (queue.length < 100) queue.push([full, payload]);
      }
    } catch (e) {}
  }

  /* ---------- seen: 50% for 1 s, once per asset per page view ---------- */
  var seenDone = {};
  var timers = new Map();
  var io = null;

  function seenKey(el) {
    return (el.getAttribute('data-asset-id') || '') + '#' + (el.getAttribute('data-h26-product-id') || '');
  }

  function fireSeen(el) {
    timers.delete(el);
    var key = seenKey(el);
    if (seenDone[key]) { if (io) io.unobserve(el); return; }
    if (document.visibilityState === 'hidden') {
      el.__h26obs = 0;
      if (io) io.unobserve(el);
      return;
    }
    seenDone[key] = 1;
    if (io) io.unobserve(el);
    track(el.getAttribute('data-h26-seen') || 'section_view', null, el);
  }

  function onIntersect(entries) {
    entries.forEach(function (en) {
      var el = en.target;
      // Review GPT-1-05 (Oct 16): an asset taller than twice the viewport can never be 50% visible, so it
      // also counts as seen when it fills half the viewport.
      var vh = (en.rootBounds && en.rootBounds.height) || window.innerHeight || 0;
      var on = en.isIntersecting && (en.intersectionRatio >= SEEN_RATIO || (vh > 0 && en.intersectionRect.height >= vh * SEEN_RATIO));
      if (on && !timers.has(el)) {
        timers.set(el, setTimeout(function () { fireSeen(el); }, SEEN_MS));
      } else if (!on && timers.has(el)) {
        clearTimeout(timers.get(el));
        timers.delete(el);
      }
    });
  }

  function observe(root) {
    if (!io) return;
    try {
      var list = [];
      if (root && root.nodeType === 1 && root.hasAttribute('data-asset-id')) list.push(root);
      if (root && root.querySelectorAll) list = list.concat([].slice.call(root.querySelectorAll('[data-asset-id]')));
      list.forEach(function (el) {
        if (el.__h26obs) return;
        if (el.getAttribute('data-h26-seen') === 'none') return;
        if (seenDone[seenKey(el)]) return;
        el.__h26obs = 1;
        io.observe(el);
      });
    } catch (e) {}
  }

  function startSeen() {
    if (!('IntersectionObserver' in window)) return;
    var steps = [];
    for (var k = 0; k <= 20; k++) steps.push(k / 20);
    io = new IntersectionObserver(onIntersect, { threshold: steps });
    observe(document.body);
    try {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            if (added[j].nodeType === 1) observe(added[j]);
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
    // a hidden tab never counts: pending timers drop, and every unseen asset is
    // observed afresh when the tab is visible again
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        timers.forEach(function (t, el) { clearTimeout(t); el.__h26obs = 0; io.unobserve(el); });
        timers.clear();
      } else {
        observe(document.body);
      }
    });
  }

  /* ---------- click delegation ---------- */
  function onClick(e) {
    try {
      var el = e.target && e.target.closest ? e.target.closest('[data-h26-event]') : null;
      if (!el) return;
      // a <summary> click on an open <details> closes it: not an *_open event (GEMQA-006)
      if (el.tagName === 'SUMMARY' && el.parentElement && el.parentElement.tagName === 'DETAILS' && el.parentElement.open) return;
      var fields = {};
      for (var i = 0; i < EXTRA.length; i++) {
        var a = el.getAttribute('data-h26-' + EXTRA[i].replace(/_/g, '-'));
        if (a !== null) fields[EXTRA[i]] = a;
      }
      track(el.getAttribute('data-h26-event'), fields, el);
    } catch (err) {}
  }

  /* ---------- first screen timing (Oct 26, REQ-010): oo_first_screen, once per page view ----------
     value = largest paint (ms), count = first paint (ms), index = page load (ms), state = inapp_ig | inapp_fb | browser.
     Existing fields only, so the pixel and BigQuery need no change. Sent at the first tap/scroll/key, when the
     page is hidden, or 8 s after load, whichever comes first. */
  (function firstScreen() {
    try {
      if (!('PerformanceObserver' in window)) return;
      var lcp = null, fcp = null, sent = false, ua = navigator.userAgent || '';
      var where = /Instagram/i.test(ua) ? 'inapp_ig' : (/FBAN|FBAV|FB_IAB|FBIOS/i.test(ua) ? 'inapp_fb' : 'browser');
      try { new PerformanceObserver(function (l) { var e = l.getEntries(); if (e.length) lcp = Math.round(e[e.length - 1].startTime); }).observe({ type: 'largest-contentful-paint', buffered: true }); } catch (e) {}
      try { new PerformanceObserver(function (l) { l.getEntries().forEach(function (e) { if (e.name === 'first-contentful-paint') fcp = Math.round(e.startTime); }); }).observe({ type: 'paint', buffered: true }); } catch (e) {}
      var send = function () {
        if (sent || (lcp === null && fcp === null)) return;
        sent = true;
        var nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
        track('first_screen', { value: lcp, count: fcp, index: nav && nav.loadEventEnd ? Math.round(nav.loadEventEnd) : null, state: where }, null);
      };
      ['pointerdown', 'keydown', 'scroll'].forEach(function (t) { window.addEventListener(t, function () { setTimeout(send, 0); }, { once: true, passive: true, capture: true }); });
      document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') send(); });
      window.addEventListener('pagehide', send);
      window.addEventListener('load', function () { setTimeout(send, 8000); });
    } catch (e) {}
  })();

  window.OOH26 = window.OOH26 || {};
  window.OOH26.__events = true;
  window.OOH26.track = track;
  window.OOH26.context = context;
  window.OOH26.observe = observe;

  document.addEventListener('click', onClick, true);
  function init() { startSeen(); flush(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('load', flush);
  setTimeout(flush, 3000);
})();
